import { Kbd, SearchInput } from '@mgrvtechgroup/admin-ds';
import { useId, useState } from 'react';

/**
 * 목록 검색칸 — Enter로 확정(C29)하고, 확정 전 입력이 남아 있으면 "Enter로 검색" 안내를 띄운다.
 * 입력만 하고 기다리다 "검색이 안 된다"고 오해하는 일을 막는다. 입력값은 이 안에서만 들고,
 * 확정·지우기 때만 onSearch로 알린다(바깥 상태 — 보통 URL의 q — 를 타이핑마다 바꾸지 않는다).
 *
 * 안내 위치:
 * - `beside`(기본): 입력칸 옆. 안내가 떠도 옆 요소가 밀리지 않도록 그 자리를 미리 비워 두므로,
 *   안내가 없을 때는 옆 요소와의 간격이 벌어진다.
 * - `inside`: 입력칸 안 오른쪽(지우기 버튼 왼쪽). 자리를 따로 잡지 않아 간격이 벌어지지 않고,
 *   안내가 떠도 레이아웃이 움직이지 않는다. 검색칸 바로 옆에 다른 컨트롤이 오는 툴바에 쓴다.
 */
export function SearchField({
  value,
  onSearch,
  placeholder,
  'aria-label': ariaLabel,
  hint = 'beside',
}: {
  /** 확정된 검색어(URL 등). 바깥에서 바뀌면(초기화·뒤로가기) 입력칸도 그 값으로 맞춘다. */
  value: string;
  /** 확정 검색어(앞뒤 공백 제거). 지우기는 빈 문자열. */
  onSearch: (value: string) => void;
  placeholder: string;
  'aria-label'?: string;
  hint?: 'beside' | 'inside';
}) {
  const hintId = useId();
  const [text, setText] = useState(value);
  const [synced, setSynced] = useState(value);
  // 렌더 중 동기화 — effect로 맞추면 한 프레임 동안 이전 입력이 보인다.
  if (synced !== value) {
    setSynced(value);
    setText(value);
  }
  const pending = text.trim() !== value;
  const inside = hint === 'inside';

  const input = (
    <SearchInput
      value={text}
      onValueChange={setText}
      onSearch={(next) => {
        onSearch(next.trim());
      }}
      onClear={() => {
        onSearch('');
      }}
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      aria-describedby={pending ? hintId : undefined}
      size="sm"
      wrapperClassName={inside ? 'w-full' : 'w-full max-w-xs'}
      // 안내가 입력 글자 위에 겹치지 않도록 그만큼 오른쪽 여백을 늘린다.
      className={inside && pending ? 'pr-[136px]' : undefined}
    />
  );

  if (inside) {
    return (
      <div className="relative w-full max-w-xs">
        {input}
        {pending && (
          // 지우기 버튼(입력이 있을 때만 나온다) 바로 왼쪽. 클릭은 입력칸으로 흘려보낸다.
          <span
            id={hintId}
            className={`caption text-muted-foreground pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center whitespace-nowrap ${
              text === '' ? 'right-3' : 'right-[38px]'
            }`}
          >
            <Kbd>Enter</Kbd>로 검색
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-sm items-center gap-2">
      {input}
      {pending && (
        <span id={hintId} className="caption text-muted-foreground shrink-0 whitespace-nowrap">
          <Kbd>Enter</Kbd>로 검색
        </span>
      )}
    </div>
  );
}
